import MyLocationFilled from '@ixigo/icons/MyLocationFilled';
import Backdrop from '@ixigo/iui/Backdrop';
import IconButton from '@ixigo/iui/IconButton';
import cn from '@repo/shared/utils/cn';
import { useCallback, useMemo, useState } from 'react';
import { getUserLocation } from '../location-services';
import type { MapLocation } from '../types';
import { createLocationIcon } from './googleMaps';
import { LocationLoader } from './LocationLoader';
import { MapLocationAutocomplete } from './MapLocationAutocomplete';
import { type UseGoogleMapParams, useGoogleMap } from './useGoogleMap';

export interface MapLocationPickerProps
	extends Omit<UseGoogleMapParams, 'onLocationChange' | 'value'> {
	/** Callback when location is confirmed */
	onConfirm: (location: MapLocation) => void;
	title: string;

	/** Custom class name */
	className?: string;
}

export function MapLocationPicker({
	onConfirm,
	className,
	title,
	...props
}: MapLocationPickerProps) {
	const [currentLocation, setCurrentLocation] = useState<
		MapLocation | undefined
	>(props.defaultValue);

	const [isExpanded, setIsExpanded] = useState(false);

	const onError = useCallback((error: Error) => {
		// TODO: handle error
		console.error(error);
	}, []);

	const { isMapLoaded, mapRef, mapInstanceRef } = useGoogleMap({
		...props,
		value: currentLocation,
		onError,
		onLocationChange: setCurrentLocation,
	});

	const pinUrl = useMemo(() => createLocationIcon(), []);

	const toggleAutocomplete = (value?: boolean) => {
		document.startViewTransition(() => {
			setIsExpanded(value ?? ((prev) => !prev));
		});
	};

	return (
		<div className={cn('flex flex-col h-full', className)}>
			<div className="flex-1 relative">
				<div className="size-full" ref={mapRef} />
				{/* Center pin overlay to avoid marker flicker while dragging */}
				{isMapLoaded && (
					<div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
						<img src={pinUrl} width={40} height={40} alt="" />
					</div>
				)}
				{isMapLoaded && (
					<IconButton
						className="absolute right-20 bottom-12 bg-primary shadow-100"
						color="subbrand"
						radius="full"
						onClick={() => {
							getUserLocation().then((location) => {
								location && mapInstanceRef.current?.setCenter(location);
							});
						}}
					>
						<MyLocationFilled />
					</IconButton>
				)}
				{!isMapLoaded && <LocationLoader />}
			</div>
			<Backdrop
				open={isExpanded}
				className="z-[1]"
				onClick={() => {
					console.log('clicked');
					toggleAutocomplete(false);
				}}
			/>

			{/* bottom-content */}
			<MapLocationAutocomplete
				open={isExpanded}
				onOpenChange={toggleAutocomplete}
				value={currentLocation}
				onChange={setCurrentLocation}
				onConfirm={onConfirm}
				className="-mt-6"
				title={title}
			/>
		</div>
	);
}
